import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { DispositionsService } from 'src/app/dashboard/modules/dispositions/dispositions.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { DashboardService } from '../../dashboard.service';
import { DispositionsModuleData } from './dispositions.model';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-dispositions',
    templateUrl: './dispositions.component.html',
    styleUrls: ['./dispositions.component.scss']
})
export class DispositionsComponent implements OnInit {
    @ViewChild('dispositionsWidgetGrid') agGrid: AgGridAngular;

    @Input() private moduleManager: ModuleManager;
    @Input() private moduleKey: string; // GUID for the instance of this module.

    public data: DispositionsModuleData = null;
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;
    // Ag-grid configuration:
    public gridOptions: GridOptions = null;
    public isFirstDataLoaded: boolean = false;

    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
    private stateSaveTimer = null; // This is used by queueStateSave for debouncing (this type is the same as returned by $timeout)


    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService, // Used to pull translations.  Most strings should be localized in the markup aspx file, but there are some strings (like AgGrid) that need to be localized in the controller code.
        private dispositionsService: DispositionsService,
        private dashboardService: DashboardService
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        await this.cvLocalizationService.GetSection("Common");

        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: settings => {
                this.loadData(false);
            },
            hasRefresh: true,
            onExport: this.onExport.bind(this),
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
        });

        const isNewPlatform = this.dashboardService.isNewPlatform;

        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
                menuTabs: this.agGridHelperService.getMenuTabItems(this.moduleManager.hasWriteAccess()),
            },
            getMainMenuItems: params => this.agGridHelperService.hideResetColumnsMenuItem(params, this.moduleManager.hasWriteAccess()),
            columnDefs: isNewPlatform ? this.createColumnDataPipeline() : this.createColumnDataLegacy(),
            onGridReady: event => {
                if (this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
                this.applyFilter();
                this.restoreColumnState();
            },
            onFirstDataRendered: event => {
                if (this.gridState == null) {
                    this.agGrid?.api.sizeColumnsToFit(); // using the ag-grid sizeColumns instead of sizeColumnsSmart since the row data is loaded, and will cause the columns widths to be uneven
                }
            },
            isExternalFilterPresent: () => this.filterValue.length > 0,
            doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue),
            onGridSizeChanged: event => {
                if (this.moduleManager?.container.isResizeWaiting)
                    this.agGridHelperService.sizeColumnsSmart(event);
                else if (this.moduleManager?.container.isFullscreen)
                    this.agGridHelperService.sizeColumnsSmart(event);
                else
                    this.restoreColumnState();
            },
            onSortChanged: () => this.tableStateChange(),
            onColumnMoved: () => this.tableStateChange(),
            onColumnResized: (event) => {
                if (event.source === "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen))
                    this.tableStateChange();
            },
        },
        {
            enableCopy: true
        });
        this.moduleManager.showLoader = true;

        // Module Configuration:
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
                this.restoreColumnState();
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });
    }

    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.agGrid.api.setGridOption("quickFilterText" ,this.filterValue);
        }
    }


    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    public async onExport(format: ModuleManagerExportFormat) {
        this.moduleManager.showLoader = true;
        const sort = {
            order: []
        };
        const agState = this.agGrid?.api.getColumnState();
        for (const state of agState) {
            sort.order[state.sortIndex] = [Number(state.colId), state.sort];
        }
        const response = await this.dispositionsService.getDispositionsDataForExport(this.moduleKey, format, JSON.stringify(sort));
        this.moduleManager.showLoader = false;
        return response;
    }

    tableStateChange() {
        this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
        this.queueStateSave();
    }

    queueStateSave() {
        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }
        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    private restoreColumnState() {
        if (!this.gridState?.columns || !this.agGrid?.api || this.agGrid?.api.getColumnDefs().length < 1) {
            return;
        }
        this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
    };

    private createColumnDataPipeline() {
        return [
            {
                field: "Name",
                colId: "0",
                headerName: "Disposition",
                menuTabs: [],
                resizable: true
            },
            {
                field: "Count",
                colId: "1",
                headerName: "Handled",
                menuTabs: [],
                resizable: true
            },
            {
                field: "Percentage",
                colId: "2",
                headerName: "Disposition %",
                menuTabs: [],
                resizable: true,
                valueFormatter: (params) => {
                    let ret = Number(params.value * 100).toFixed(2) + "%";
                    if (isNaN(params.value))
                        ret = "0.00%";
                    return ret;
                }
            },
            {
                field: "AHT",
                colId: "3",
                headerName: "AHT",
                menuTabs: [],
                resizable: true
            },
        ];
    }

    private createColumnDataLegacy() {
        return [
            {
                field: "Name",
                colId: "0",
                headerName: "Disposition",
                menuTabs: [],
                resizable: true
            },
            {
                field: "Count",
                colId: "1",
                headerName: "DispositionCount",
                menuTabs: [],
                resizable: true
            },
            {
                field: "Percentage",
                colId: "2",
                headerName: "Disposition %",
                menuTabs: [],
                resizable: true,
                valueFormatter: (params) => {
                    let ret = Number(params.value * 100).toFixed(2) + "%";
                    if (isNaN(params.value))
                        ret = "0.00%";
                    return ret;
                }
            },
            {
                field: "AHT",
                colId: "3",
                headerName: "AHT",
                menuTabs: [],
                resizable: true
            },
        ];
    }

    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    }

    private async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.dispositionsService.loadDispositionsData(
                this.moduleKey,
                isAutoRefresh,
                this.isFirstLoad
            ) as DispositionsModuleData;

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data.DispositionsRows);
            }
            //else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.

            this.moduleManager.container.showDateRange = true;
            this.isFirstDataLoaded = true;
            this.isFirstLoad = false;
            this.moduleManager.showLoader = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
        }
    }
}
